import type { BodyProfile, EventInput, Food, FoodDraft, FoodInput, LifeEvent, User } from '@loggy/shared';

/**
 * The signed-in person, or null.
 *
 * `credentials: 'same-origin'` is what sends the session cookie. Without it the
 * browser omits it and every request looks signed out.
 */
export async function fetchMe(): Promise<User | null> {
  const res = await fetch('/api/me', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`Could not load your account (${res.status}).`);
  return (await res.json()) as User;
}

export async function logout(): Promise<void> {
  const res = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!res.ok && res.status !== 401) {
    throw new Error(`Could not sign out (${res.status}).`);
  }
}

export interface WithingsStatus {
  connected: boolean;
  lastSyncedAt: string | null;
  externalUserId: string | null;
}

export interface WithingsSyncResult {
  inserted: number;
  skipped: number;
  syncedAt: string;
}

/**
 * A plain navigation target, never a fetch.
 *
 * The browser must follow the redirect itself so Withings' own consent screen
 * sees a real navigation, the same reason `/api/auth/google/start` is a link
 * rather than an XHR.
 */
export const withingsConnectUrl = '/api/withings/connect';

export async function fetchWithingsStatus(): Promise<WithingsStatus> {
  const res = await fetch('/api/withings/status', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Could not load your Withings connection (${res.status}).`);
  return (await res.json()) as WithingsStatus;
}

export async function syncWithingsNow(): Promise<WithingsSyncResult> {
  const res = await fetch('/api/withings/sync', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Withings sync failed (${res.status}).`);
  return (await res.json()) as WithingsSyncResult;
}

export async function disconnectWithings(): Promise<void> {
  const res = await fetch('/api/withings/disconnect', {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`Could not disconnect Withings (${res.status}).`);
  }
}

/**
 * The node/entries contract, shared verbatim with the API and chart briefs
 * of this wave (see the wave plan). Declared here rather than imported from
 * `@loggy/shared` because the API route that produces it is being built by a
 * sibling in parallel and has not landed in the shared package.
 */

/** One plotted point. */
export interface SeriesPoint {
  /** ISO-8601. The x position derives from THIS, never from array index. */
  recordedAt: string;
  /** The reading, or the bucket's aggregate when aggregated. */
  value: number;
  /** Readings behind this point. 1 for a raw reading. */
  count: number;
  /** Only on aggregated buckets: the spread the aggregate conceals. */
  min?: number;
  max?: number;
  /** Only on aggregated buckets: the earliest and latest reading INSIDE it,
   *  in time order. What a within-bucket change is measured between. */
  first?: number;
  last?: number;
  /** When those two were recorded. A change of 0.6 kg means something
   *  different across six days than across two, and the bucket's own
   *  timestamp only says which week it belongs to, not which days it spans. */
  firstAt?: string;
  lastAt?: string;
  /** Only on raw readings. */
  source?: 'manual' | 'withings' | 'import';
}

/** GET /api/nodes/:id/entries */
export interface EntriesResponse {
  entries: SeriesPoint[];
  resolution: 'raw' | 'daily' | 'weekly';
  /** true when entries are buckets rather than individual readings. */
  aggregated: boolean;
  truncated: boolean;
}

export type EntriesResolution = EntriesResponse['resolution'];

export type NodeKind = 'metric' | 'collection' | 'document';

/**
 * One row of `GET /api/nodes`; also what `POST` and `PATCH` return.
 *
 * `unit`/`precision`/`latestValue` are nullable because non-metric kinds
 * (a family tree, a research note) have none of them — only a `metric` node
 * carries a unit at all.
 */
export interface NodeSummary {
  id: string;
  typeKey: string;
  title: string;
  kind: NodeKind;
  unit: string | null;
  precision: number | null;
  latestValue: number | null;
  latestRecordedAt: string | null;
  entryCount: number;
  source: 'withings' | 'manual';
}

export async function fetchNodes(): Promise<NodeSummary[]> {
  const res = await fetch('/api/nodes', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Could not load your nodes (${res.status}).`);
  const body = (await res.json()) as { nodes: NodeSummary[] };
  return body.nodes;
}

/** `POST /api/nodes`. `title` omitted falls back to the catalog entry's own. */
export interface CreateNodeInput {
  typeKey: string;
  title?: string;
}

export async function createNode(input: CreateNodeInput): Promise<NodeSummary> {
  const res = await fetch('/api/nodes', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not add this node (${res.status}).`));
  return (await res.json()) as NodeSummary;
}

/** `PATCH /api/nodes/:id`. Only the title can change — the type is fixed at creation. */
export interface UpdateNodeInput {
  title: string;
}

export async function updateNode(id: string, input: UpdateNodeInput): Promise<NodeSummary> {
  const res = await fetch(`/api/nodes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not rename this node (${res.status}).`));
  return (await res.json()) as NodeSummary;
}

export async function deleteNode(id: string): Promise<void> {
  const res = await fetch(`/api/nodes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  // 204 No Content is the success case and has no body to read.
  if (res.status === 204) return;
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not delete this node (${res.status}).`));
}

export interface EntriesQuery {
  /** ISO-8601. Omit for no lower bound. */
  from?: string;
  /** ISO-8601. Omit for "up to now". */
  to?: string;
  resolution: EntriesResolution;
}

export async function fetchNodeEntries(
  nodeId: string,
  query: EntriesQuery,
): Promise<EntriesResponse> {
  const params = new URLSearchParams({ resolution: query.resolution });
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const res = await fetch(`/api/nodes/${encodeURIComponent(nodeId)}/entries?${params.toString()}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Could not load readings (${res.status}).`);
  return (await res.json()) as EntriesResponse;
}

/**
 * The goals contract, shared verbatim with the orbit-endpoint and orbit-view
 * briefs of Wave 6 (see the wave plan). Declared here — rather than imported
 * from `@loggy/shared` — for the same reason `SeriesPoint`/`EntriesResponse`
 * above are: the route that produces `GoalOutput` is being built by a sibling
 * in parallel and has not landed yet. `GET /api/orbit` is that sibling's own
 * concern and is intentionally not wired here — GoalForm never calls it.
 */

/** What POST and PATCH /api/goals return. */
export interface GoalOutput {
  id: string;
  nodeId: string;
  title: string;
  targetValue: number;
  baselineValue: number | null;
  targetDate: string | null;
  direction: 'lower-is-better' | 'higher-is-better' | 'neutral';
  achievedAt: string | null;
  createdAt: string;
}

/** POST /api/goals  and  PATCH /api/goals/:id */
export interface GoalInput {
  nodeId: string; // POST only
  title: string;
  targetValue: number;
  targetDate: string | null; // ISO date, null = open-ended
  /** When the goal starts counting from. POST only; omitted means now. */
  startDate?: string;
}

/**
 * Reads `{ error: string }` off a failed response so a rejected submission
 * can show what the server actually said — a validation message, a
 * conflict — rather than a generic "something went wrong". Falls back to
 * `fallback` when the body isn't JSON (a proxy error page, an empty 500).
 */
async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
  } catch {
    // No JSON body to read a message from — use the fallback below.
  }
  return fallback;
}

export async function createGoal(input: GoalInput): Promise<GoalOutput> {
  const res = await fetch('/api/goals', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not create this goal (${res.status}).`));
  return (await res.json()) as GoalOutput;
}

/**
 * `nodeId` is deliberately excluded here: it is POST-only per the shared
 * contract, and a goal's own house style is to make a fact that can't
 * change unable to be sent, rather than send it and rely on the server to
 * ignore it.
 */
export async function updateGoal(id: string, input: Omit<GoalInput, 'nodeId'>): Promise<GoalOutput> {
  const res = await fetch(`/api/goals/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not update this goal (${res.status}).`));
  return (await res.json()) as GoalOutput;
}

export async function deleteGoal(id: string): Promise<void> {
  const res = await fetch(`/api/goals/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not delete this goal (${res.status}).`));
}

// ---------------------------------------------------------------------------
// The orbit
// ---------------------------------------------------------------------------

export interface OrbitGoal {
  id: string;
  title: string;
  targetValue: number;
  baselineValue: number | null;
  /** When the baseline was captured — what "since the start" measures from. */
  startDate: string;
  targetDate: string | null;
  direction: 'lower-is-better' | 'higher-is-better' | 'neutral';
  achievedAt: string | null;
  /** Computed server-side from the shared rule, never recalculated here. */
  progress: {
    fraction: number | null;
    currentValue: number | null;
    remaining: number | null;
    achieved: boolean;
    daysRemaining: number | null;
  };
}

export interface OrbitItem {
  nodeId: string;
  typeKey: string;
  title: string;
  kind: NodeKind;
  unit: string | null;
  precision: number | null;
  latestValue: number | null;
  latestRecordedAt: string | null;
  entryCount: number;
  source: 'withings' | 'manual';
  /** null when this node is merely watched rather than chased — always null
   *  for non-metric kinds, which have no notion of a target. */
  goal: OrbitGoal | null;
}

/**
 * `spaceId` omitted loads the caller's own orbit (`readOnly` always false).
 * Passed, it loads someone else's — only ever a space they've been granted
 * access to via a share, and only the nodes granted to them within it; the
 * server 404s otherwise. The two never merge: a caller switches between them,
 * it does not combine them.
 */
export async function fetchOrbit(spaceId?: string): Promise<{ items: OrbitItem[]; readOnly: boolean }> {
  const query = spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : '';
  const res = await fetch(`/api/orbit${query}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load this orbit (${res.status}).`));
  return (await res.json()) as { items: OrbitItem[]; readOnly: boolean };
}

// ---------------------------------------------------------------------------
// Sharing — invites, who I share with, and who shares with me
// ---------------------------------------------------------------------------

export interface InviteSummary {
  id: string;
  invitedEmail: string;
  status: 'pending' | 'accepted' | 'declined' | 'revoked';
  expiresAt: string;
  createdAt: string;
  fromUserId: string;
  fromDisplayName: string;
  spaceId: string;
  nodes: { id: string; title: string }[];
}

/** One person the caller shares their own dashboard with, and which of the
 *  caller's own nodes they've been granted. */
export interface ShareRecipient {
  userId: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
  nodes: { id: string; title: string }[];
}

/** One row of `GET /api/people`: the caller themself, then anyone who shares
 *  their dashboard with the caller — the set a person selector switches
 *  between. */
export interface Person {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  spaceId: string;
  isSelf: boolean;
  sharedNodeCount: number;
}

export async function createInvite(input: {
  email: string;
  nodeIds: string[];
}): Promise<{ invite: InviteSummary; link: string }> {
  const res = await fetch('/api/invites', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not send this invite (${res.status}).`));
  return (await res.json()) as { invite: InviteSummary; link: string };
}

export async function fetchIncomingInvites(): Promise<InviteSummary[]> {
  const res = await fetch('/api/invites/incoming', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load your invites (${res.status}).`));
  const body = (await res.json()) as { invites: InviteSummary[] };
  return body.invites;
}

export async function fetchSentInvites(): Promise<InviteSummary[]> {
  const res = await fetch('/api/invites/sent', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load the invites you've sent (${res.status}).`));
  const body = (await res.json()) as { invites: InviteSummary[] };
  return body.invites;
}

/** Shared plumbing for the three invite actions below — same shape, same
 *  method, different verb and different fallback wording for the one message
 *  the caller sees when the server gives no message of its own. */
async function postInviteAction(
  id: string,
  action: 'accept' | 'decline' | 'revoke',
  fallback: string,
): Promise<InviteSummary> {
  const res = await fetch(`/api/invites/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, fallback));
  const body = (await res.json()) as { invite: InviteSummary };
  return body.invite;
}

export async function acceptInvite(id: string): Promise<InviteSummary> {
  return postInviteAction(id, 'accept', `Could not accept this invite (${id}).`);
}

export async function declineInvite(id: string): Promise<InviteSummary> {
  return postInviteAction(id, 'decline', `Could not decline this invite (${id}).`);
}

export async function revokeInvite(id: string): Promise<InviteSummary> {
  return postInviteAction(id, 'revoke', `Could not revoke this invite (${id}).`);
}

export async function acceptInviteByToken(token: string): Promise<InviteSummary> {
  const res = await fetch('/api/invites/accept-by-token', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not accept this invite (${res.status}).`));
  const body = (await res.json()) as { invite: InviteSummary };
  return body.invite;
}

export async function fetchShares(): Promise<ShareRecipient[]> {
  const res = await fetch('/api/shares', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load who you share with (${res.status}).`));
  return (await res.json()) as ShareRecipient[];
}

/** Replaces the whole set of nodes granted to `userId` — never a partial
 *  add or remove, matching the PUT semantics of the endpoint itself. */
export async function updateShare(userId: string, nodeIds: string[]): Promise<ShareRecipient> {
  const res = await fetch(`/api/shares/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ nodeIds }),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Could not update what this person can see (${res.status}).`));
  }
  return (await res.json()) as ShareRecipient;
}

export async function revokeShare(userId: string): Promise<void> {
  const res = await fetch(`/api/shares/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  // 204 No Content is the success case and has no body to read.
  if (res.status === 204) return;
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Could not remove this person's access (${res.status}).`));
  }
}

export async function fetchPeople(): Promise<Person[]> {
  const res = await fetch('/api/people', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load people (${res.status}).`));
  return (await res.json()) as Person[];
}

// ---------------------------------------------------------------------------
// Life events — shared verbatim with the node-detail work of this wave (see
// the wave plan). `LifeEvent`/`EventInput` themselves come straight from
// `@loggy/shared`, since that contract has already landed there.
// ---------------------------------------------------------------------------

/** GET /api/nodes/:id/events */
export interface EventsResponse {
  events: LifeEvent[];
}

export async function fetchNodeEvents(
  nodeId: string,
  window?: { from: string; to: string },
): Promise<LifeEvent[]> {
  const params = new URLSearchParams();
  if (window) {
    params.set('from', window.from);
    params.set('to', window.to);
  }
  const query = params.toString();
  const res = await fetch(
    `/api/nodes/${encodeURIComponent(nodeId)}/events${query ? `?${query}` : ''}`,
    {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    },
  );
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load these events (${res.status}).`));
  const body = (await res.json()) as EventsResponse;
  return body.events;
}

export async function createNodeEvent(nodeId: string, input: EventInput): Promise<LifeEvent> {
  const res = await fetch(`/api/nodes/${encodeURIComponent(nodeId)}/events`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not add this event (${res.status}).`));
  return (await res.json()) as LifeEvent;
}

export async function updateNodeEvent(
  nodeId: string,
  eventId: string,
  input: EventInput,
): Promise<LifeEvent> {
  const res = await fetch(
    `/api/nodes/${encodeURIComponent(nodeId)}/events/${encodeURIComponent(eventId)}`,
    {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not update this event (${res.status}).`));
  return (await res.json()) as LifeEvent;
}

export async function deleteNodeEvent(nodeId: string, eventId: string): Promise<void> {
  const res = await fetch(
    `/api/nodes/${encodeURIComponent(nodeId)}/events/${encodeURIComponent(eventId)}`,
    {
      method: 'DELETE',
      credentials: 'same-origin',
    },
  );
  // 204 No Content is the success case and has no body to read.
  if (res.status === 204) return;
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not delete this event (${res.status}).`));
}

// ---------------------------------------------------------------------------
// Body profile — the optional Mifflin–St Jeor inputs a weight goal uses to
// estimate a healthy pace. `BodyProfile` comes straight from `@loggy/shared`,
// which also owns `bodyProfileInputSchema` for client-side validation before
// any of this is called.
// ---------------------------------------------------------------------------

/** GET /api/body-profile. `null` means no profile has been saved yet — an
 *  ordinary state, not an error. */
export async function fetchBodyProfile(): Promise<BodyProfile | null> {
  const res = await fetch('/api/body-profile', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load your body profile (${res.status}).`));
  const body = (await res.json()) as { profile: BodyProfile | null };
  return body.profile;
}

/** PUT /api/body-profile. Creates or replaces the profile outright. */
export async function saveBodyProfile(input: BodyProfile): Promise<BodyProfile> {
  const res = await fetch('/api/body-profile', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not save your body profile (${res.status}).`));
  const body = (await res.json()) as { profile: BodyProfile };
  return body.profile;
}

export async function deleteBodyProfile(): Promise<void> {
  const res = await fetch('/api/body-profile', {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  // 204 No Content is the success case and has no body to read.
  if (res.status === 204) return;
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, `Could not remove your body profile (${res.status}).`));
  }
}

// ---------------------------------------------------------------------------
// Food library — the owner's groceries, one row per product. `Food`,
// `FoodInput` and `FoodDraft` all come straight from `@loggy/shared`, which
// also owns `foodInputSchema` for client-side validation before any of this
// is called.
// ---------------------------------------------------------------------------

/** GET /api/nodes/:id/foods */
export interface FoodsResponse {
  foods: Food[];
}

/** GET /api/nodes/:id/foods/lookup?barcode=... */
export interface FoodLookupResponse {
  draft: FoodDraft;
}

/**
 * A save was refused because the barcode already names another food in this
 * library. Carries that food's id so the caller can offer to open it instead
 * of just reporting the clash.
 */
export class DuplicateBarcodeError extends Error {
  foodId: string;
  constructor(message: string, foodId: string) {
    super(message);
    this.name = 'DuplicateBarcodeError';
    this.foodId = foodId;
  }
}

/** Reads a 409's `{ error, foodId }` body, falling back to a plain message
 *  when the response has no body to parse — same reasoning as
 *  `readErrorMessage` above, but this one also needs the food id. */
async function readDuplicateBarcodeError(res: Response): Promise<DuplicateBarcodeError> {
  try {
    const body = (await res.json()) as { error?: string; foodId?: string };
    if (typeof body.foodId === 'string') {
      return new DuplicateBarcodeError(
        typeof body.error === 'string' && body.error.trim()
          ? body.error
          : 'This barcode is already in your library.',
        body.foodId,
      );
    }
  } catch {
    // No JSON body to read — fall through to the generic message below.
  }
  return new DuplicateBarcodeError('This barcode is already in your library.', '');
}

export async function fetchNodeFoods(nodeId: string): Promise<Food[]> {
  const res = await fetch(`/api/nodes/${encodeURIComponent(nodeId)}/foods`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load this food library (${res.status}).`));
  const body = (await res.json()) as FoodsResponse;
  return body.foods;
}

export async function createNodeFood(nodeId: string, input: FoodInput): Promise<Food> {
  const res = await fetch(`/api/nodes/${encodeURIComponent(nodeId)}/foods`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.status === 409) throw await readDuplicateBarcodeError(res);
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not add this food (${res.status}).`));
  return (await res.json()) as Food;
}

export async function updateNodeFood(nodeId: string, foodId: string, input: FoodInput): Promise<Food> {
  const res = await fetch(
    `/api/nodes/${encodeURIComponent(nodeId)}/foods/${encodeURIComponent(foodId)}`,
    {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (res.status === 409) throw await readDuplicateBarcodeError(res);
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not update this food (${res.status}).`));
  return (await res.json()) as Food;
}

export async function deleteNodeFood(nodeId: string, foodId: string): Promise<void> {
  const res = await fetch(
    `/api/nodes/${encodeURIComponent(nodeId)}/foods/${encodeURIComponent(foodId)}`,
    {
      method: 'DELETE',
      credentials: 'same-origin',
    },
  );
  // 204 No Content is the success case and has no body to read.
  if (res.status === 204) return;
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not delete this food (${res.status}).`));
}

/**
 * Looks a barcode up against Open Food Facts, through our own API (never the
 * network directly — see CLAUDE.md on Open Food Facts). `null` means the
 * product genuinely isn't there yet, which is an ordinary result the caller
 * offers to fill in by hand; anything else failing throws, since that is
 * Open Food Facts not answering rather than the product not existing.
 */
export async function lookupFoodBarcode(nodeId: string, barcode: string): Promise<FoodDraft | null> {
  const res = await fetch(
    `/api/nodes/${encodeURIComponent(nodeId)}/foods/lookup?barcode=${encodeURIComponent(barcode)}`,
    {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    },
  );
  if (res.status === 404) return null;
  if (res.status === 409) throw await readDuplicateBarcodeError(res);
  if (!res.ok) {
    throw new Error(
      await readErrorMessage(res, `Open Food Facts did not answer. Try again, or fill it in by hand.`),
    );
  }
  const body = (await res.json()) as FoodLookupResponse;
  return body.draft;
}
